/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useContext, type ReactNode } from 'react';
import { CustomerPortalContext, CustomerPortalAction } from '../../../contexts/CustomerPortalContext';
import { InfoBanner } from '../../InfoBanner/InfoBanner';
import { BannerButton } from '../BannerButton/BannerButton';
import { GTMContext } from '../../../contexts/GTMContext';
import { GTM_EVENT_UPGRADE_PLAN } from '../../../constants/GTMEventNames';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';

type CreateAccountBannerProps = {
	allFeaturesAvailableInPlan: boolean;
	arePremiumFeaturesSelected: boolean;
	signUpVariant: 'sidebar' | 'steps';
	hideButtons?: boolean;
};

export function CreateAccountBanner( {
	allFeaturesAvailableInPlan,
	arePremiumFeaturesSelected,
	signUpVariant,
	hideButtons
}: CreateAccountBannerProps ): ReactNode {
	const {
		isLoggedIn,
		isTrialActive,
		plan,
		customerPortalAccount,
		getUpgradePlanUrl,
		performCustomerPortalAction
	} = useContext( CustomerPortalContext );
	const { triggerGTMEvent } = useContext( GTMContext );
	const { onSelectedInstallMethodChange, onSelectedCodeOutputChange } = useContext( InstallationSettingsContext );

	const organizationId = customerPortalAccount?.organizations?.at( 0 )?.id;
	const subscriptionId = customerPortalAccount?.subscriptions?.at( 0 )?.details.id;

	function handleUpgradePlanClick() {
		triggerGTMEvent( {
			event: GTM_EVENT_UPGRADE_PLAN
		} );
	}

	function handleSignUpClick() {
		performCustomerPortalAction( CustomerPortalAction.SIGN_UP, {
			variant: 'steps',
			onSuccess: () => {
				onSelectedInstallMethodChange( null );
				onSelectedCodeOutputChange( null );
			}
		} );
	}

	if ( isTrialActive && !arePremiumFeaturesSelected ) {
		return (
			<InfoBanner data-testid={ 'account-banner-trial-no-premium-features' } variant={ 'info' }>
				Your <b>14-day trial is active!</b> You can test all Premium Features for free! Go back to previous
				steps and <b>select some more features.</b>
			</InfoBanner>
		);
	}

	if ( isTrialActive && arePremiumFeaturesSelected ) {
		return (
			<InfoBanner data-testid={ 'account-banner-trial-premium-features' } variant={ 'success' }>
				Your <b>14-day trial is active!</b> Enjoy full access to all features, with all required premium fields
				prefilled in the snippets.
			</InfoBanner>
		);
	}

	if ( !isLoggedIn && !arePremiumFeaturesSelected ) {
		return (
			<InfoBanner
				data-testid={ 'account-banner-not-logged-no-premium-features' } variant={ 'info' } buttonRight={ hideButtons ? null : (
					<BannerButton dataTestId={ 'ab-not-logged-no-premium-features-button' } onClick={ handleSignUpClick }>
						Sign up for trial
					</BannerButton>
				) }
			>
				{ signUpVariant === 'sidebar' && (
					<span data-testid='ab-not-logged-no-premium-features-sidebar-button-text'>
						Psst… you can test premium features with a free 14-day trial.
					</span>
				) }
				{ signUpVariant === 'steps' && (
					<span data-testid='ab-not-logged-no-premium-features-steps-button-text'>
						Sign up for a <b>free trial</b> to test all premium features of CKEditor 5.
					</span>
				) }
			</InfoBanner>
		);
	}

	if ( !isLoggedIn && arePremiumFeaturesSelected ) {
		return (
			<InfoBanner
				data-testid={ 'account-banner-not-logged-in' } variant={ 'warning' } buttonRight={ hideButtons ? null : (
					<BannerButton dataTestId={ 'ab-not-logged-in-button' } onClick={ handleSignUpClick }>
						Sign up for trial
					</BannerButton>
				) }
			>
				You have premium features in your editor.
				Sign up for a <b>free trial</b> to use all premium features of CKEditor 5.
			</InfoBanner>
		);
	}

	if ( !allFeaturesAvailableInPlan ) {
		return (
			<InfoBanner
				variant={ 'warning' } data-testid={ 'account-banner-not-all-features-in-plan' } buttonRight={ hideButtons ? null : (
					<BannerButton
						dataTestId={ 'ab-not-all-features-in-plan-button' }
						onClick={ handleUpgradePlanClick }
						href={ getUpgradePlanUrl( organizationId!, subscriptionId ) }
					>
						Upgrade
					</BannerButton>
				) }
			>
				Some features <b>are not available</b> in your plan!
				Remove these features or <b>upgrade your plan</b> to use them!
			</InfoBanner>
		);
	}

	if ( arePremiumFeaturesSelected ) {
		return (
			<InfoBanner variant={ 'success' } data-testid={ 'account-banner-all-features-in-plan' }>
				All selected <b>Premium Features are covered by your</b> plan
				and we prefilled all premium fields in the snippets.
			</InfoBanner>
		);
	}

	if ( plan === 'free' ) {
		return (
			<InfoBanner
				variant={ 'info' } data-testid={ 'account-banner-free-plan' } buttonRight={ hideButtons ? null : (
					<BannerButton
						dataTestId={ 'ab-free-plan-button' }
						onClick={ handleUpgradePlanClick }
						href={ getUpgradePlanUrl( organizationId!, subscriptionId ) }
					>
						Upgrade
					</BannerButton>
				) }
			>
				It looks like you're currently on the <b>CKEditor 5 Free plan.</b> Upgrade now to unlock access to Premium Features.
			</InfoBanner>
		);
	}

	if ( plan === 'basic' ) {
		return (
			<InfoBanner variant={ 'info' } data-testid={ 'account-banner-basic-plan' }>
				It looks like you're currently on the <b>CKEditor 5 Essential plan</b> { ' ' }
				which gives you access to our Premium Features. Go back to the previous step to select more features.
			</InfoBanner>
		);
	}

	if ( plan === 'premium' ) {
		return (
			<InfoBanner variant={ 'info' } data-testid={ 'account-banner-premium-plan' }>
				It looks like you're currently on the <b>CKEditor 5 Premium plan</b> { ' ' }
				which gives you access to our Premium Features. Go back to the previous step to select more features.
			</InfoBanner>
		);
	}
}
